// Heal-on-mount: walks the user's local projects once per session and, for
// each GC-owned project whose linked estimate has no portalTokens, mints a
// portallink__<token> via ensurePortalLinkForProject.
//
// Why this exists: before the V1-parity auto-create landed in
// useProjectMutations.add / useEstimateMutations.convertToProject, many
// projects were created without a portal link. Receivers of those projects
// see "Portal link not available" because the sender has no portallink__ doc
// for the rules engine to surface via findPortalTokenForReceivedProject.
//
// This hook closes the gap for pre-existing data: best-effort, one-shot per
// uid, runs after app state loads. Received projects are skipped (the sender
// owns the portal for their side). Projects without a linked estimate are
// also skipped — ensurePortalLinkForProject no-ops without an estimate, and
// creating a stub estimate silently would be surprising.
//
// Idempotent: if a portal link already exists, ensurePortalLinkForProject
// still calls syncPortalWithProject which only writes fields that can be
// safely re-stamped. No destructive side effects.

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { ensurePortalLinkForProject } from '../lib/portalSync'
import { getShared, setShared } from '../lib/db/shared'
import type { AppState } from '../types/firestore'

const healed = new Set<string>()

export function useHealMissingPortalLinks(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()

  useEffect(() => {
    if (!uid || !isReady) return
    if (healed.has(uid)) return

    // Gate on appstate being loaded — otherwise we bail and re-run when the
    // state arrives (useEffect re-runs on qc change would be too noisy; we
    // use a short retry instead).
    let retries = 0
    const maxRetries = 20 // 10s total @ 500ms
    const tick = async () => {
      const state = qc.getQueryData<AppState>(qk.appstate(uid))
      if (!state) {
        if (retries++ < maxRetries) setTimeout(tick, 500)
        return
      }
      if (healed.has(uid)) return
      healed.add(uid)

      // Walk projects, heal each GC-owned project whose linked estimate has
      // no tokens. Sequential with a tiny delay so we don't hammer Firestore
      // on big accounts.
      let healedCount = 0
      for (const p of state.projects) {
        const raw = p as unknown as { _received?: boolean }
        if (raw._received) continue
        const est = state.estimates.find((e) => e.projId === p.id)
        if (!est) continue
        const hasToken = Array.isArray(est.portalTokens) && est.portalTokens.length > 0
        if (hasToken) continue
        // Re-read the latest state each iteration in case saveSlice from the
        // previous mint has landed.
        const latest = qc.getQueryData<AppState>(qk.appstate(uid)) ?? state
        try {
          await ensurePortalLinkForProject(uid, p.id, latest)
          healedCount += 1
          // Tiny breather so we don't rate-limit ourselves.
          await new Promise((r) => setTimeout(r, 150))
        } catch (err) {
          console.warn('[heal-portal] project', p.id, 'failed', err)
        }
      }
      if (healedCount > 0) {
        console.warn('[heal-portal] minted', healedCount, 'portal link(s) for pre-existing projects')
      }

      // ── Schema-v2 backfill ──────────────────────────────────────────────
      // Upgrade any existing portallink__ docs that are missing participantUids,
      // schemaVersion: 2, or linkedClientUid. Idempotent: docs that already
      // have participantUids, schemaVersion 2, AND linkedClientUid (when the
      // project has a linked client) are skipped.
      let upgradedCount = 0
      const latest2 = qc.getQueryData<AppState>(qk.appstate(uid)) ?? state
      for (const p of latest2.projects) {
        const rawP = p as unknown as { _received?: boolean }
        if (rawP._received) continue
        const est2 = latest2.estimates.find((e) => e.projId === p.id)
        if (!est2 || !Array.isArray(est2.portalTokens)) continue

        // Resolve linked client uid from Client row's raw linkedUid field.
        // This field is stamped by the link-approval flow onto the client
        // record so we don't need a secondary Firestore lookup here.
        const linkedClientRow = p.clientId != null
          ? latest2.clients.find((c) => c.id === p.clientId)
          : undefined
        const resolvedLinkedClientUid = linkedClientRow?.linkedUid

        for (const token of est2.portalTokens) {
          const docId = `portallink__${token}`
          try {
            const data = await getShared<{
              ownerUid?: string
              linkedClientUid?: string
              participantUids?: string[]
              schemaVersion?: number
            }>(docId)
            if (!data || data.ownerUid !== uid) continue

            // Determine what needs to be written.
            const needsSchemaUpgrade = !data.participantUids || data.schemaVersion !== 2
            const needsLinkedClient = resolvedLinkedClientUid !== undefined
              && data.linkedClientUid === undefined

            // Skip if already fully up to date.
            if (!needsSchemaUpgrade && !needsLinkedClient) continue

            // Build the updated participantUids — include the linked client uid
            // if we now know it and it's not already in the array.
            const existingParticipants = Array.isArray(data.participantUids)
              ? data.participantUids
              : [uid]
            const linkedUidForDoc = data.linkedClientUid ?? resolvedLinkedClientUid
            const participantUids = linkedUidForDoc !== undefined
              && !existingParticipants.includes(linkedUidForDoc)
              ? [...existingParticipants, linkedUidForDoc]
              : existingParticipants

            const patch: Record<string, unknown> = {
              participantUids,
              schemaVersion: 2,
            }
            // Only write linkedClientUid when we're filling it in for the
            // first time — don't overwrite an existing value.
            if (needsLinkedClient && resolvedLinkedClientUid !== undefined) {
              patch.linkedClientUid = resolvedLinkedClientUid
            }

            await setShared(docId, patch)
            upgradedCount += 1
            await new Promise((r) => setTimeout(r, 150))
          } catch (err) {
            console.warn('[heal-portal] upgrade v2 failed for token', token, err)
          }
        }
      }
      if (upgradedCount > 0) {
        console.warn('[heal-portal] upgraded', upgradedCount, 'portal link(s) to schemaVersion 2 / added linkedClientUid')
      }
    }

    void tick()
  }, [uid, isReady, qc])
}
